import React from 'react';
import { Box, Typography, Card, CardContent, Radio } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { TimeSettingsPreset, TIME_SETTINGS_PRESETS } from './presets/timeSettingsPresets';
import PresetParameterPreview from './PresetParameterPreview';

interface TimeSettingsPresetSelectorProps {
  selectedPreset: string;
  onPresetChange: (presetId: string, settings: {
    slope: number;
    intercept: number;
    window: number;
    onceOffProcessingTime: number;
    processingTimes: { seatType: string; processingTimeSeconds: number }[];
  }) => void;
}

const TimeSettingsPresetSelector: React.FC<TimeSettingsPresetSelectorProps> = ({
  selectedPreset,
  onPresetChange
}) => {
  const { t } = useTranslation('map');
  const handlePresetSelect = (preset: TimeSettingsPreset) => {
    onPresetChange(preset.id, {
      slope: preset.slope,
      intercept: preset.intercept,
      window: preset.window,
      onceOffProcessingTime: preset.onceOffProcessingTime,
      processingTimes: preset.processingTimes
    });
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" sx={{ mb: 2 }}>
        {t('modals.timeSettingsPreset.title')}
      </Typography>
      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
        {t('modals.timeSettingsPreset.subtitle')}
      </Typography>
      
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(4, 1fr)', 
        gap: 1.5 
      }}>
        {TIME_SETTINGS_PRESETS.map((preset) => (
          <Card
            key={preset.id}
            sx={{
              cursor: 'pointer',
              border: selectedPreset === preset.id ? '2px solid #FFA726' : '1px solid #e0e0e0',
              backgroundColor: selectedPreset === preset.id ? '#F5F5F5' : '#ffffff',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                borderColor: '#FB8C00',
                backgroundColor: '#F5F5F5',
                transform: 'translateY(-1px)',
                boxShadow: 2
              }
            }}
            onClick={() => handlePresetSelect(preset)}
          >
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Radio
                  checked={selectedPreset === preset.id}
                  sx={{
                    p: 0,
                    mr: 0.5,
                    color: '#FFA726',
                    '&.Mui-checked': {
                      color: '#FFA726',
                    },
                  }}
                  size="small"
                />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                  {preset.name}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                <PresetParameterPreview
                  preset={preset}
                  selected={selectedPreset === preset.id}
                  width={80}
                  height={40}
                />
              </Box>
              
              <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.2, fontSize: '0.7rem' }}>
                {preset.description}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Box>
  );
};

export default TimeSettingsPresetSelector; 